import { useEffect, useState, FC, useRef } from 'react';

import { useSession } from '@eneco/auth';
import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import RichText from '@eneco/components/RichText/RichText';
import {
  ConsumptionTypeDto,
  deleteEnecoBeXapiSiteApiV1AccountsByAccountNumberMeterReadings,
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierProducts,
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberMeterReadings,
  GetServiceDeliveryPointOverviewForAddressResponse,
  MeterReadingType,
} from '@eneco/dc-be/client';
import useAuthenticatedDCBE from '@eneco/dc-be/hooks/useAuthenticatedDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { useFormatter } from '@eneco/i18n';
import {
  AlertDialog,
  Box,
  ButtonLink,
  Heading,
  IconButton,
  NotificationBox,
  Popover,
  Stack,
  Table,
  Text,
  TextLink,
  Button,
} from '@eneco/sparky';
import { DeleteIcon, InfoIcon } from '@eneco/sparky/icons';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import {
  ConsumptionGraphicalOverviewRendering,
  MeterReadingsTable,
} from '@sitecore/types/ConsumptionGraphicalOverview';
import { useSWRConfig } from 'swr';

import { useAddressOptions } from '../../../../hooks/useAddressOptions';
import { useConsumptionGraphTimeFrame } from '../hooks/useConsumptionGraphTimeFrame';

export type MeterReadingsTableProps = {
  consumptionType: ConsumptionTypeDto;
  fields: MeterReadingsTable;
  serviceDeliveryData: GetServiceDeliveryPointOverviewForAddressResponse | null;
};

const MeterReadingsTableWrapper: FC<MeterReadingsTableProps> = ({ consumptionType, serviceDeliveryData }) => {
  const selectedEnergyType = consumptionType === 'Injection' ? 'Electricity' : consumptionType;

  const selectedSdp = serviceDeliveryData?.serviceDeliveryPoints?.find(sdp => sdp.type === selectedEnergyType);

  const selectedEan = selectedSdp?.ean || '';
  const isSmr3Meter = selectedSdp?.meterRegime?.current === 'Smr3';
  const hasExclusiveNightRegister =
    selectedSdp?.meterRegisterTypes?.some(registerType => registerType === 'ExclusiveNight') ?? false;

  if (consumptionType === 'Total' || !selectedEan) {
    return;
  }

  return (
    <MeterReadingsTableComponent
      selectedEan={selectedEan}
      consumptionType={consumptionType}
      isSmr3Meter={isSmr3Meter}
      hasExclusiveNightRegister={hasExclusiveNightRegister}
    />
  );
};

const MeterReadingsTableComponent = ({
  selectedEan,
  consumptionType,
  isSmr3Meter,
  hasExclusiveNightRegister,
}: {
  selectedEan: string;
  consumptionType: ConsumptionTypeDto;
  isSmr3Meter: boolean;
  hasExclusiveNightRegister: boolean;
}) => {
  const {
    fields: { meterReadingsTable },
  } = useContent<ConsumptionGraphicalOverviewRendering>();
  const {
    selectedAccount: { crmAccountNumber: accountNumber },
  } = useSelfServiceAccount();
  const { data: session } = useSession();
  const { date, format } = useFormatter();
  const query = {
    eans: [selectedEan],
  };
  const { trackEventBE } = useTracking();
  const [showModal, setShowModal] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [dataByYear, setDataByYear] = useState<Record<string, typeof unwrappedData>>({});
  const { mutate } = useSWRConfig();
  const { isCurrentAccountReader } = useSelfServiceAccount();
  const { addressIdentifier } = useAddressOptions();
  const { timeFrame, startDate, endDate } = useConsumptionGraphTimeFrame();
  const [deleteParams, setDeleteParams] = useState<{
    accountNumber: string;
    date: string;
    ean: string;
  } | null>(null);
  let showDeletedMeterReadingNotification = false;

  const { data } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberMeterReadings,
    {
      path: {
        accountNumber,
      },
      query,
    },
    [`/accounts/${accountNumber}/meter-readings`, selectedEan],
    session,
  );

  const { data: productData } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierProducts,
    {
      path: {
        accountNumber: accountNumber,
        addressIdentifier: addressIdentifier,
      },
      query: {
        eans: [selectedEan],
      },
    },
    [`/accounts/${accountNumber}/delivery-addresses/${addressIdentifier}/products`, selectedEan],
    session,
  );

  const unwrappedData = unwrapData(data);
  const unwrappedProductData = unwrapData(productData);

  const currentEanIsActive = unwrappedProductData?.activeProducts?.length === 1;

  useEffect(() => {
    if (unwrappedData === null) return;

    const map: Record<string, typeof unwrappedData> = {};
    unwrappedData.forEach(item => {
      const year = new Date(item.date!).getFullYear().toString();
      if (!map[year]) {
        map[year] = [];
      }
      map[year].push(item);
    });
    setDataByYear(map);
  }, [unwrappedData]);

  const hasTracked = useRef(false);
  useEffect(() => {
    if (hasTracked.current) return;
    hasTracked.current = true;
    trackEventBE('be_energy_monitor_visible', {
      status: 'pageView',
      data: { userIsLoggedIn: !!session, energyMonitorVisible: true },
    });
  }, [session, trackEventBE]);

  if (unwrappedData === null) {
    return;
  }

  const meterReadingOriginMap: Record<MeterReadingType, string> = {
    Official: meterReadingsTable.originOfficialText?.value ?? '',
    Informative: meterReadingsTable.originInformativeText?.value ?? '',
    CustomerAdded: meterReadingsTable.originUserText?.value ?? '',
    RLP: meterReadingsTable.originRlpText?.value ?? '',
  };

  const indexesSortOrder = [
    'consumption-th',
    'offtake-th',
    'consumption-hi',
    'offtake-hi',
    'consumption-lo',
    'offtake-lo',
    'consumption-ex',
    'offtake-ex',
    'production-th',
    'injection-th',
    'production-hi',
    'injection-hi',
    'production-lo',
    'injection-lo',
    'production-ex',
    'injection-ex',
  ];

  const unSortedIndexes: string[] = Array.from(
    new Set(
      unwrappedData.flatMap(
        item =>
          item.indexValues
            ?.filter(iv => typeof iv.direction === 'string' && typeof iv.meterTimeFrameType === 'string')
            .map(iv => `${iv.direction}-${iv.meterTimeFrameType}`) ?? [],
      ),
    ),
  );

  const sortedIndexes = unSortedIndexes.sort(
    (a, b) => indexesSortOrder.indexOf(a.toLowerCase()) - indexesSortOrder.indexOf(b.toLowerCase()),
  );

  // Sort the entries within each year by descending date
  Object.keys(dataByYear).forEach(year => {
    dataByYear[year]?.sort((a, b) => new Date(b.date!).getTime() - new Date(a.date!).getTime());
  });

  const sortedYears = Object.keys(dataByYear).sort((a, b) => Number(b) - Number(a));

  const meterReadingTableText =
    consumptionType === 'Gas'
      ? meterReadingsTable.meterReadingGasTableText?.value
      : meterReadingsTable.meterReadingElectricityTableText?.value;

  const handleRemoveMeterReadingClick = (accountNumber: string, date: string, ean: string) => {
    setDeleteParams({ accountNumber, date, ean });
    setShowModal(true);
  };

  const handleLoadMore = async () => {
    const oldestYear = Object.keys(dataByYear).sort((a, b) => Number(a) - Number(b))[0];
    const yearItems = dataByYear[oldestYear];
    const oldestDate = yearItems?.[yearItems.length - 1]?.date;

    if (!oldestDate) return;

    const endDateObj = new Date(oldestDate);
    endDateObj.setDate(endDateObj.getDate() - 1);
    const endDateQuery = endDateObj.toISOString().split('T')[0];

    const startDateObj = new Date(oldestDate);
    startDateObj.setFullYear(startDateObj.getFullYear() - 2);
    const startDateQuery = startDateObj.toISOString().split('T')[0];

    setIsLoadingMore(true);
    try {
      const result = await getEnecoBeXapiSiteApiV1AccountsByAccountNumberMeterReadings({
        path: { accountNumber },
        query: { eans: [selectedEan], startDate: startDateQuery, endDate: endDateQuery },
      });

      const newItems = result?.data?.data;
      if (!newItems?.length) {
        setHasMoreData(false);
        return;
      }

      setDataByYear(prev => {
        const updated = { ...prev };
        newItems.forEach(item => {
          if (!item.date) return;
          const year = new Date(item.date).getFullYear().toString();
          if (!updated[year]) {
            updated[year] = [];
          }
          const isDuplicate = updated[year]?.some(existing => existing.date === item.date && existing.ean === item.ean);
          if (!isDuplicate) {
            updated[year] = [...(updated[year] ?? []), item];
          }
        });
        return updated;
      });
    } finally {
      setIsLoadingMore(false);
    }
  };

  const currentYear = new Date().getFullYear().toString();

  const deleteMeterReading = async (
    accountNumber: string,
    date: string,
    ean: string,
    meterReadingsTable: MeterReadingsTable,
  ) => {
    const { error } = await deleteEnecoBeXapiSiteApiV1AccountsByAccountNumberMeterReadings({
      query: { ean, date },
      path: { accountNumber },
    });

    if (!error) {
      const year = new Date(date).getFullYear().toString();

      setDataByYear(prev => {
        if (prev == null) return prev;

        const filtered = prev[year]?.filter(r => !(r.date === date && r.ean === ean));

        const { [year]: _, ...rest } = prev;

        return filtered?.length ? { ...rest, [year]: filtered } : rest;
      });

      const consumptionGraphCacheKey = [
        `/accounts/${accountNumber}/delivery-addresses/${addressIdentifier}/consumption`,
        timeFrame,
        startDate,
        endDate,
        consumptionType,
      ].join();

      mutate(`/accounts/${accountNumber}/meter-readings`, true); // table
      mutate(consumptionGraphCacheKey, true); // graph

      showDeletedMeterReadingNotification = true;
    }
  };

  return (
    <Box paddingTop={4}>
      <Stack gap="8">
        <Heading as="h3" size="S">
          {meterReadingsTable.meterReadingTableTitle.value}
        </Heading>
        {!isCurrentAccountReader && currentEanIsActive && !isSmr3Meter && !hasExclusiveNightRegister && (
          <Stack direction="row" gap="8" alignY="start" alignX={'justify'} wrap>
            <Stack.Item grow={{ initial: false, md: true }}>
              <Text size="BodyM">{format(meterReadingTableText, { eanCode: selectedEan })}</Text>
            </Stack.Item>
            <Stack.Item>
              <ButtonLink
                action="primary"
                tone="onLight"
                href={`${meterReadingsTable.addMeterReadingsLink.value.href}?ean=${selectedEan}&address=${addressIdentifier}`}
                onClick={() => trackEventBE('energy_monitor_add', { status: 'click' })}>
                <Text>{meterReadingsTable.addMeterReadingButtonText.value}</Text>
              </ButtonLink>
            </Stack.Item>
          </Stack>
        )}
        {showDeletedMeterReadingNotification && (
          <NotificationBox
            isAlert={false}
            variant={'success'}
            title={'Meterstand verwijderd [not in sitecore]'}
            text={<RichText html={'De meterstand is weg'} />}
          />
        )}

        {unwrappedData.length && (
          <>
            <Table>
              <Table.Header>
                {[
                  <Text key="datum">{meterReadingsTable.dateTableHeaderText.value}</Text>,
                  ...sortedIndexes.map(timeFrame => (
                    <Text align={'center'} key={timeFrame}>
                      {
                        meterReadingsTable.energyOptionsList.value.enum.find(
                          item => item.name.toLowerCase() === timeFrame?.toLowerCase(),
                        )?.label
                      }
                    </Text>
                  )),
                  <Text align={'center'} key="meterReadingType">
                    {meterReadingsTable.originText.value}
                  </Text>,
                  <Text align={'center'} key="remove">
                    {meterReadingsTable.deleteValueText.value}
                  </Text>,
                ]}
              </Table.Header>

              {sortedYears.map(year => (
                <Table.Section
                  key={year}
                  title={year}
                  isCollapsible
                  isOpen={openSections[year] ?? year === currentYear}
                  setOpen={value =>
                    setOpenSections(prev => ({
                      ...prev,
                      [year]: typeof value === 'function' ? value(prev[year] ?? year === currentYear) : value,
                    }))
                  }>
                  {dataByYear[year]?.map((item, idx) => {
                    const indexValueMap = item.indexValues?.reduce<Record<string, string>>((acc, iv) => {
                      if (iv) {
                        acc[iv.direction + '-' + iv.meterTimeFrameType] = iv.value ? iv.value + '' : '-';
                      }
                      return acc;
                    }, {});

                    return (
                      <Table.Row key={idx}>
                        {[
                          <Text key="date">{date.medium(item.date!)}</Text>,
                          ...sortedIndexes.map(timeFrame => (
                            <Text align={'center'} key={timeFrame}>
                              {indexValueMap?.[timeFrame ?? '']}
                            </Text>
                          )),
                          <Text align={'center'} key="type">
                            {(item.meterReadingType && meterReadingOriginMap[item.meterReadingType]) || ''}
                          </Text>,
                          <Text align={'center'}>
                            {item.meterReadingType !== 'CustomerAdded' || isCurrentAccountReader ? (
                              '/'
                            ) : (
                              <IconButton
                                label={'Delete'}
                                size="regular"
                                onClick={() => {
                                  handleRemoveMeterReadingClick(accountNumber, item.date!, selectedEan);
                                }}>
                                <DeleteIcon size="medium" />
                              </IconButton>
                            )}
                          </Text>,
                        ]}
                      </Table.Row>
                    );
                  })}
                </Table.Section>
              ))}
            </Table>

            {hasMoreData && (
              <Stack direction="row" gap="8" alignY="start" alignX={'justify'} wrap>
                <Stack.Item grow={{ initial: false, md: true }}>
                  <Button onClick={handleLoadMore} isLoading={isLoadingMore}>
                    {meterReadingsTable.loadMoreText?.value}
                  </Button>
                </Stack.Item>
              </Stack>
            )}

            <Text>
              {meterReadingsTable.originMoreInfoText.value}
              <Popover
                side="top"
                size="large"
                title=""
                trigger={<TextLink rightIcon={<InfoIcon size="small" verticalAlign="baseline" />}>{''}</TextLink>}>
                <RichText html={meterReadingsTable.originMoreInfoPopoverContent.value} />
              </Popover>
            </Text>
          </>
        )}
      </Stack>
      <AlertDialog
        title={meterReadingsTable.meterReadingsConfirmationDialog.value.title ?? 'Ben je zeker? [not in sitecore]'}
        description={
          <RichText
            html={format(
              meterReadingsTable.meterReadingsConfirmationDialog.value.content ??
                'Je staat op het punt om je meterstand voor {date} te verwijderen [not in sitecore]',
              {
                date: date.medium(deleteParams?.date ?? new Date()),
              },
            )}
          />
        }
        isOpen={showModal}
        setOpen={setShowModal}
        confirmText={
          meterReadingsTable.meterReadingsConfirmationDialog.value.submitButtonText ??
          'Meterstand verwijderen [not in sitecore]'
        }
        onConfirm={() => {
          if (deleteParams) {
            trackEventBE('energy_monitor_delete_reading', { status: 'click' });
            deleteMeterReading(deleteParams.accountNumber, deleteParams.date, deleteParams.ean, meterReadingsTable);
          }
        }}
        onDeny={() => {}}
        denyText={
          meterReadingsTable.meterReadingsConfirmationDialog.value.cancelButtonText ?? 'Annuleren [not in sitecore]'
        }
      />
    </Box>
  );
};

export default MeterReadingsTableWrapper;
